import React, { useEffect, useState } from "react";
import { BsFillPersonLinesFill } from "react-icons/bs";
import { CiEdit } from "react-icons/ci";
import { GoCommentDiscussion } from "react-icons/go";
import { HiMiniAcademicCap } from "react-icons/hi2";
import { MdEventAvailable, MdWork } from "react-icons/md";
import { SlDocs } from "react-icons/sl";
import { FiArchive, FiCopy, FiExternalLink, FiSettings } from "react-icons/fi";
import { FaFileExcel } from "react-icons/fa";
import { Form, Spinner } from "react-bootstrap";

import "../1.3 actividades/Actividad.css";
import "./Iniciativa.css";
import SeccionGeneral from "./SeccionGeneral";
import SeccionEquipo from "./SeccionEquipo";
import SeccionParticipantes from "./SeccionParticipantes";
import SeccionDocumentos from "../1.3 actividades/subcomponents/SeccionDocumentos";
import SeccionComentarios from "../1.3 actividades/subcomponents/SeccionComentarios";
import SeccionAsistencia from "./SeccionAsistencia";
import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";

const tabs = [
  ["general", "Información general", BsFillPersonLinesFill],
  ["coordinacion", "Coordinación y organizaciones", MdWork],
  ["participantes", "Participantes", HiMiniAcademicCap],
  ["asistencia", "Asistencia", MdEventAvailable],
  ["comentarios", "Comentarios", GoCommentDiscussion],
  ["documentos", "Documentos", SlDocs],
];

const productKey = (initiative) => (
  initiative?.tipo?.nombre || "product"
).toLocaleLowerCase("es");

const IniciativaView = ({ iniciativa, onIniciativaUpdated, onBusyChange }) => {
  const initialHash = window.location.hash.substring(1);
  const [activeTab, setActiveTab] = useState(
    tabs.some(([key]) => key === initialHash) ? initialHash : "general",
  );
  const [data, setData] = useState(iniciativa);
  const [copied, setCopied] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSetting, setSavingSetting] = useState("");
  const [settingsError, setSettingsError] = useState("");
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [actionError, setActionError] = useState("");
  const busy = Boolean(savingSetting) || downloadingExcel;
  const description = (data.descripcion || "").trim();
  const contentItems = (data.contenidos || "")
    .split(/\r?\n/)
    .map((item) => item.replace(/^\s*[•*-]\s*/, "").trim())
    .filter(Boolean);

  useEffect(() => {
    setData(iniciativa);
    setCopied(false);
    setActionError("");
  }, [iniciativa]);

  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);

  const copyEnrollmentUrl = async () => {
    setActionError("");
    try {
      await navigator.clipboard.writeText(data.url_inscripcion);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_error) {
      setActionError("No se pudo copiar el enlace. Puedes abrirlo y copiarlo desde el navegador.");
    }
  };

  const downloadExcel = async () => {
    setDownloadingExcel(true);
    setActionError("");
    try {
      const response = await fetch(getApiUrl(`api/iniciativas/${data.id}/excel/`), {
        credentials: "include",
      });
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `Iniciativa-${data.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (_error) {
      setActionError("No se pudo descargar el Excel de esta iniciativa.");
    } finally {
      setDownloadingExcel(false);
    }
  };

  const updateSetting = async (field, checked) => {
    if (busy) return;
    setSavingSetting(field);
    setSettingsError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/iniciativas/${data.id}/?settings=true`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({ [field]: checked }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const firstError = Object.values(body)[0];
        throw new Error(
          (Array.isArray(firstError) ? firstError[0] : firstError)
          || body.detail
          || "No se pudo actualizar la configuración.",
        );
      }
      const updatedInitiative = { ...data, ...body };
      setData(updatedInitiative);
      onIniciativaUpdated?.(updatedInitiative);
    } catch (requestError) {
      setSettingsError(requestError.message);
    } finally {
      setSavingSetting("");
    }
  };

  return (
    <div id="actividad" className={savingSetting ? "initiative-detail-saving" : ""} aria-busy={Boolean(savingSetting)}>
      {savingSetting && <div className="initiative-settings-overlay" role="status"><Spinner animation="border" /><span>{savingSetting === "activa" ? "Archivando..." : "Guardando configuración..."}</span></div>}
      <div className="initiative-detail-hero">
        <div className="initiative-detail-visual">
          {data.imagen ? <img src={data.imagen} alt={data.nombre} /> : <span>{data.nombre?.charAt(0)}</span>}
        </div>
        <div className="initiative-detail-copy">
          <span className={`initiative-type initiative-type-${productKey(data)}`}>{data.tipo?.nombre || "Sin tipo"}</span>
          <h1>{data.nombre}</h1>
          <div>
            <span>{data.oficio || "Sin oficio"}</span>
            <span>{data.comuna || "Sin comuna"}</span>
            <span>{data.region || "Sin región"}</span>
          </div>
        </div>
        <div className="initiative-detail-actions">
          <a href={`/iniciativas/${data.id}/editar`} className={`initiative-detail-edit${busy ? " disabled" : ""}`} title="Editar iniciativa" aria-disabled={busy} onClick={(event) => { if (busy) event.preventDefault(); }}>
            <CiEdit /> <span>Editar</span>
          </a>
          <button type="button" className="initiative-detail-config" title="Configuraciones" onClick={() => setShowSettings((current) => !current)} aria-expanded={showSettings} disabled={busy}>
            <FiSettings /> <span>Configurar</span>
          </button>
          <button type="button" className="initiative-detail-archive" title="Archivar iniciativa" onClick={() => updateSetting("activa", false)} disabled={busy || !data.activa}>
            {savingSetting === "activa" ? <Spinner animation="border" size="sm" /> : <FiArchive />} <span>Archivar</span>
          </button>
          <button type="button" className="initiative-detail-excel" title="Descargar Excel" aria-label="Descargar Excel de la iniciativa" onClick={downloadExcel} disabled={busy}>
            {downloadingExcel ? <Spinner animation="border" size="sm" /> : <FaFileExcel />} <span>Excel</span>
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="initiative-quick-settings" aria-label="Configuraciones de la iniciativa">
          <div className="initiative-quick-setting">
            <div><strong>Inscripción pública</strong><small>{productKey(data) === "programa" ? "Los Programas reciben postulaciones mediante su proceso de selección." : "Habilita el enlace público para inscribirse."}</small></div>
            <div className="initiative-setting-control">{savingSetting === "inscripcion_habilitada" && <Spinner animation="border" size="sm" />}<Form.Check type="switch" id={`initiative-enrollment-${data.id}`} checked={productKey(data) !== "programa" && Boolean(data.inscripcion_habilitada)} onChange={(event) => updateSetting("inscripcion_habilitada", event.target.checked)} disabled={busy || productKey(data) === "programa"} aria-label="Habilitar inscripción pública" /></div>
          </div>
          <div className="initiative-quick-setting">
            <div><strong>Mostrar en el calendario</strong><small>No cambia su visibilidad en los listados.</small></div>
            <div className="initiative-setting-control">{savingSetting === "mostrar_en_calendario" && <Spinner animation="border" size="sm" />}<Form.Check type="switch" id={`initiative-calendar-${data.id}`} checked={Boolean(data.mostrar_en_calendario)} onChange={(event) => updateSetting("mostrar_en_calendario", event.target.checked)} disabled={busy} aria-label="Mostrar iniciativa en el calendario" /></div>
          </div>
          <div className="initiative-quick-setting">
            <div><strong>Usar imagen estándar</strong><small>Define la imagen de Convocatorias sin eliminar la presentación cargada.</small></div>
            <div className="initiative-setting-control">{savingSetting === "usar_imagen_estandar" && <Spinner animation="border" size="sm" />}<Form.Check type="switch" id={`initiative-standard-image-${data.id}`} checked={Boolean(data.usar_imagen_estandar)} onChange={(event) => updateSetting("usar_imagen_estandar", event.target.checked)} disabled={busy} aria-label="Usar imagen estándar en Convocatorias" /></div>
          </div>
          {settingsError && <p>{settingsError}</p>}
        </div>
      )}

      {data.url_inscripcion && (
        <div className="initiative-enrollment-actions">
          <a className="initiative-enrollment-open" href={data.url_inscripcion} target="_blank" rel="noreferrer">
            <FiExternalLink /> Ir al formulario
          </a>
          <button type="button" className="initiative-enrollment-copy" onClick={copyEnrollmentUrl} disabled={busy}>
            <FiCopy /> Copiar link
          </button>
          {copied && <span className="initiative-enrollment-copied">¡Copiado!</span>}
        </div>
      )}
      {actionError && <p className="initiative-action-error">{actionError}</p>}
      {productKey(data) === "programa" && data.proceso_seleccion && (
        <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
          <a className="btn btn-primary" href="/procesos">
            Ir al proceso de selección
          </a>
          <span className={`badge ${data.proceso_seleccion.estado === "abierto" ? "bg-success" : "bg-secondary"}`}>
            {data.proceso_seleccion.estado === "abierto" ? "Selección abierta" : "Selección cerrada"}
          </span>
        </div>
      )}

      {(description || contentItems.length > 0) && (
        <section className="initiative-description-card">
          {description && (
            <div className="initiative-description-copy">
              <span>RESUMEN</span>
              <h2>Descripción breve</h2>
              <p>{description}</p>
            </div>
          )}
          {contentItems.length > 0 && (
            <div className="initiative-content-list">
              <h3>Tabla de Contenidos</h3>
              <ul>{contentItems.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>
            </div>
          )}
        </section>
      )}

      <div className="actividad-tabs">
        <ul className="nav nav-tabs">
          {tabs.map(([key, label, Icon]) => (
            <li className="nav-item" key={key}>
              <a
                className={`nav-link ${activeTab === key ? "active" : ""}`}
                href={`#${key}`}
                title={label}
                aria-label={label}
                onClick={() => setActiveTab(key)}
              >
                <Icon />
                <span>{label}</span>
              </a>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {activeTab === "general" && <SeccionGeneral iniciativa={data} />}
          {activeTab === "coordinacion" && <SeccionEquipo iniciativa={data} />}
          {activeTab === "participantes" && (
            <SeccionParticipantes
              iniciativa={data}
              onIniciativaUpdated={setData}
            />
          )}
          {activeTab === "asistencia" && <SeccionAsistencia iniciativa={data} />}
          {activeTab === "documentos" && (
            <SeccionDocumentos
              actividadId={data.id}
              detailPath="api/iniciativas"
              detailAction="documentos"
              collectionPath="api/iniciativas-documentos/"
              foreignKey="iniciativa"
            />
          )}
          {activeTab === "comentarios" && (
            <SeccionComentarios
              actividadId={data.id}
              detailPath="api/iniciativas"
              detailAction="comentarios"
              collectionPath="api/iniciativas-comentarios/"
              foreignKey="iniciativa"
              textField="texto"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default IniciativaView;
